(() => {
  const hashPattern = /^[a-f0-9]{64}$/;
  const maxAge = 24 * 60 * 60 * 1000;
  function create({storage, key, crypto = window.crypto, fetch = window.fetch.bind(window), now = Date.now, receiptUrl = '/search?view=gifting-receipt', confirmationTimeoutMs = 10000}) {
    const hash = async value => [...new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(value)))].map(byte => byte.toString(16).padStart(2, '0')).join('');
    function read() {
      const raw = storage?.getItem(key);
      if (!raw) return null;
      let attempt;
      try { attempt = JSON.parse(raw); } catch (_) { throw new Error('Your previous submission could not be restored. Please check with our team before submitting again.'); }
      if (attempt.version !== 1 || !hashPattern.test(attempt.key) || !hashPattern.test(attempt.payloadHash) || typeof attempt.selectedJson !== 'string' || !['PREPARED','SUBMITTED','CONFIRMED'].includes(attempt.state) || !Number.isFinite(attempt.at) || now() < attempt.at || now() - attempt.at >= maxAge) throw new Error('Your previous submission needs to be checked by our team before another request is sent.');
      return attempt;
    }
    function save(attempt) {
      if (!storage) throw new Error('Your browser could not preserve this submission. Keep this page open and contact our team.');
      const raw = JSON.stringify(attempt);
      storage.setItem(key, raw);
      if (storage.getItem(key) !== raw) throw new Error('Your browser could not preserve this submission. Keep this page open and contact our team.');
      return attempt;
    }
    async function prepare(fields, gifts) {
      if (!Array.isArray(gifts) || !gifts.length) throw new Error('Select a gift before submitting.');
      const payloadHash = await hash(JSON.stringify({fields, gifts}));
      const previous = read();
      if (previous) {
        if (previous.payloadHash !== payloadHash) throw new Error('A previous request has not been confirmed. Check its status before changing your details or sending another request.');
        if (await hash(previous.selectedJson) !== previous.key) throw new Error('Your saved submission could not be verified. Please check with our team.');
        return previous;
      }
      const nonce = [...crypto.getRandomValues(new Uint8Array(32))].map(byte => byte.toString(16).padStart(2, '0')).join('');
      const selectedJson = JSON.stringify(gifts.map((gift, index) => index === 0 ? {...gift, captureNonce: nonce, capturePayloadHash: payloadHash} : gift));
      return save({version: 1, at: now(), payloadHash, selectedJson, key: await hash(selectedJson), state: 'PREPARED'});
    }
    function submitted(expected) {
      const attempt = read();
      if (!attempt || attempt.key !== expected.key) throw new Error('The saved submission changed. Please refresh and check its status.');
      return save({...attempt, state: 'SUBMITTED'});
    }
    async function check(expected = read()) {
      if (!expected) return false;
      if (!hashPattern.test(expected.key) || await hash(expected.selectedJson) !== expected.key) throw new Error('The saved submission could not be verified.');
      const controller = window.AbortController ? new window.AbortController() : null;
      let timeout;
      let receipt;
      try {
        receipt = await Promise.race([
          (async () => {
            const response = await fetch(`${receiptUrl}&q=${expected.key}&capture_check=${now()}`, {headers: {Accept: 'application/json'}, cache: 'no-store', credentials: 'same-origin', ...(controller ? {signal: controller.signal} : {})});
            if (!response.ok) throw new Error('Capture confirmation is temporarily unavailable. Your gift bag and details are saved.');
            return response.json();
          })(),
          new Promise((_, reject) => {
            timeout = window.setTimeout(() => {
              reject(new Error('Capture confirmation timed out. Your gift bag and details are saved.'));
              controller?.abort();
            }, confirmationTimeoutMs);
          }),
        ]);
      } finally { window.clearTimeout(timeout); }
      if (receipt?.captureKey !== expected.key || typeof receipt?.captured !== 'boolean') throw new Error('Capture confirmation could not be verified. Your gift bag and details are saved.');
      if (!receipt.captured) return false;
      const current = read();
      if (!current || current.key !== expected.key) throw new Error('The saved submission changed while it was being checked.');
      save({...current, state: 'CONFIRMED'});
      return true;
    }
    function retryAfterReload(expected) {
      const attempt = read();
      if (!attempt || attempt.key !== expected.key || attempt.state === 'CONFIRMED') throw new Error('The saved submission changed. Please check its status.');
      save({...attempt, resumeKey: attempt.key});
    }
    function takeRetry(expected) {
      const attempt = read();
      if (!attempt || attempt.key !== expected.key || attempt.resumeKey !== attempt.key) return false;
      const {resumeKey, ...pending} = attempt;
      save(pending);
      return true;
    }
    function clear() { storage?.removeItem(key); }
    return {read, prepare, submitted, check, retryAfterReload, takeRetry, clear};
  }
  window.UrbanGiftingCapture = {create};
})();
